<template>
  <div>
    <div ref="backdrop" class="danger-sign-alert__backdrop">
      <section ref="dialog" class="danger-sign-alert"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="blocks.map(block => `danger-sign-alert-${block.urgency}-titles`).join(' ')"
        :aria-describedby="blocks.map(block => `danger-sign-alert-${block.urgency}-message`).join(' ')"
        tabindex="-1">
        <div v-for="block in blocks" :key="block.urgency"
          :class="['danger-sign-alert__block', `danger-sign-alert__block--${block.urgency}`]">
          <div class="danger-sign-alert__icon" aria-hidden="true">
            <v-icon class="danger-sign-alert__alert-icon" size="30">{{ icons.mdiAlertOutline }}</v-icon>
          </div>
          <div class="danger-sign-alert__content">
            <span class="danger-sign-alert__badge">{{ $t(`triage.${block.urgency}.title`) }}</span>
            <div :id="`danger-sign-alert-${block.urgency}-titles`">
              <h2 v-if="block.signs.length === 1" class="danger-sign-alert__title">
                {{ $t(`app.danger_sign_alert.${block.urgency}.title`, {
                  name: $t(nameToken(block.signs[0])),
                }) }}
              </h2>
              <template v-else>
                <h2 class="danger-sign-alert__title">
                  {{ $t(`app.danger_sign_alert.${block.urgency}.title`, { name: '' }).trim() }}
                </h2>
                <ul class="danger-sign-alert__signs">
                  <li v-for="sign in block.signs" :key="sign.name">{{ $t(nameToken(sign)) }}</li>
                </ul>
              </template>
            </div>
            <p :id="`danger-sign-alert-${block.urgency}-message`" class="danger-sign-alert__message">
              {{ $t(`app.danger_sign_alert.${block.urgency}.message`) }}
            </p>
          </div>
        </div>
        <div class="danger-sign-alert__actions">
          <p v-if="stopHintVisible" id="danger-sign-alert-stop-hint" class="danger-sign-alert__stop-hint"
            role="alert">
            {{ $t('app.danger_sign_alert.stop_needs_patient_id') }}
          </p>
          <button type="button" class="danger-sign-alert__button"
            :aria-describedby="stopHintVisible ? 'danger-sign-alert-stop-hint' : null"
            @click="handleStop">
            {{ $t('app.danger_sign_alert.stop') }}
          </button>
          <button ref="acknowledgeButton" type="button" class="danger-sign-alert__button"
            @click="acknowledge">
            {{ $t('app.danger_sign_alert.acknowledge') }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import icons from '@/mixins/icons'
import { useDialogFocusTrap } from '@/composables/useDialogFocusTrap'
import { useDangerSignStore } from '../stores/dangerSign.store'
import { nameToken, urgencyOf } from '../utils/dangerSignPresentation'

const URGENCIES = ['urgent', 'immediate'] as const

export default defineComponent({
  name: 'DangerSignAlertDialog',
  mixins: [icons],
  props: {
    stopBlocked: { type: Boolean, default: false },
  },
  setup(props) {
    const store = useDangerSignStore()
    const stopHintShown = ref(false)
    const stopHintVisible = computed(() => stopHintShown.value && props.stopBlocked)
    watch(() => props.stopBlocked, blocked => {
      if (!blocked) stopHintShown.value = false
    })
    const backdrop = ref<HTMLElement | null>(null)
    const dialog = ref<HTMLElement | null>(null)
    const acknowledgeButton = ref<HTMLButtonElement | null>(null)
    // Only this inner node leaves the page's stacking context, so it must be removed by hand.
    onMounted(() => backdrop.value?.closest('.v-application')?.appendChild(backdrop.value))
    onBeforeUnmount(() => backdrop.value?.remove())
    useDialogFocusTrap(dialog)
    // Registered after the trap, so this wins: an Enter repeat from Next must not stop the assessment.
    onMounted(() => acknowledgeButton.value?.focus())

    const blocks = computed(() => URGENCIES
      .map(urgency => ({ urgency, signs: store.pendingAlert.filter(sign => urgencyOf(sign) === urgency) }))
      .filter(block => block.signs.length))

    return {
      backdrop,
      dialog,
      acknowledgeButton,
      blocks,
      nameToken,
      stopHintVisible,
      acknowledge: () => store.acknowledge(),
      handleStop: () => {
        if (props.stopBlocked) {
          stopHintShown.value = true
          return
        }
        store.requestStop()
      },
    }
  },
})
</script>

<style scoped src="../styles/danger-sign-alert-dialog.css"></style>
